/**
 * A hand-rolled line chart (admin spec 8, open point 5, decided in Task 1's
 * dependency table: no charting library on this surface).
 *
 * IT FORMATS NOTHING AND DECIDES NOTHING. Every string it draws -- the axis
 * maximum, each point's native `<title>`, the `aria-label` that IS the chart
 * for anyone not looking at it -- arrives in the `LinePlan` that
 * lib/metrics.ts's `seriesPlan` built, and every coordinate comes from
 * lib/chartScale.ts. What is left here is the SVG.
 *
 * That split is the Task 13 review's M1 and M2 taken together: "which number
 * goes where" is a Node-testable decision, and so is "is a bigger number drawn
 * higher" -- a question `role="img"` plus a non-empty `aria-label` cannot
 * answer, and which an inverted axis passes while drawing every peak as a
 * trough.
 *
 * No tooltips beyond the native `<title>` on each point, no zoom, no animation:
 * a desk tool read once a week does not need them, and every one of them is a
 * dependency.
 */
import {
  CHART_BASELINE, CHART_HEIGHT, CHART_PAD_TOP, CHART_PAD_X, CHART_WIDTH,
  chartMax, plotX, plotY,
} from '../../lib/chartScale';
import type { LinePlan } from '../../lib/metrics';

export function LineChart({ plan }: { plan: LinePlan }) {
  const points = plan.points;
  if (points.length === 0) return null;

  const max = chartMax(points.map((p) => p.value));
  const d = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${plotX(i, points.length).toFixed(1)},${plotY(p.value, max).toFixed(1)}`)
    .join(' ');
  const first = points[0];
  const last = points[points.length - 1];

  return (
    <svg
      className="chart chart-line"
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      role="img"
      aria-label={plan.ariaLabel}
      preserveAspectRatio="none"
    >
      <title>{plan.ariaLabel}</title>
      <line x1={CHART_PAD_X} y1={CHART_BASELINE} x2={CHART_WIDTH - CHART_PAD_X} y2={CHART_BASELINE}
        stroke="currentColor" strokeOpacity="0.25" />
      <path d={d} fill="none" stroke="var(--accent)" strokeWidth="2" />
      {points.map((p, i) => (
        <circle
          key={`${p.label}-${i}`}
          cx={plotX(i, points.length)}
          cy={plotY(p.value, max)}
          r="2.5"
          fill="var(--accent)"
        >
          <title>{p.title}</title>
        </circle>
      ))}
      <text x={CHART_PAD_X} y={CHART_HEIGHT - 8} fontSize="12" fill="currentColor" fillOpacity="0.7">
        {first?.label ?? ''}
      </text>
      <text x={CHART_WIDTH - CHART_PAD_X} y={CHART_HEIGHT - 8} fontSize="12" textAnchor="end"
        fill="currentColor" fillOpacity="0.7">
        {last?.label ?? ''}
      </text>
      <text x={CHART_PAD_X} y={CHART_PAD_TOP} fontSize="12" fill="currentColor" fillOpacity="0.7">
        {plan.maxLabel}
      </text>
    </svg>
  );
}
